import "./index.css"

import { jsxs, jsx } from 'react/jsx-runtime';
import { useDomDirection } from '@design-system/hooks';
import { isBrowser } from '@phx-husky/browser-helpers';
import classNames from 'classnames';
import objectHash from 'object-hash';
import React, { useEffect, useLayoutEffect, useContext, createContext, forwardRef, useState, useRef } from 'react';
import Swiper$1, { Scrollbar } from 'swiper';
import { Typography } from '@design-system/typography';
import { Button } from '@design-system/button';

function isObject(o) {
  return typeof o === 'object' && o !== null && o.constructor && Object.prototype.toString.call(o).slice(8, -1) === 'Object';
}

function extend(target, src) {
  const noExtend = ['__proto__', 'constructor', 'prototype'];
  Object.keys(src).filter(key => noExtend.indexOf(key) < 0).forEach(key => {
    if (typeof target[key] === 'undefined') target[key] = src[key];else if (isObject(src[key]) && isObject(target[key]) && Object.keys(src[key]).length > 0) {
      if (src[key].__swiper__) target[key] = src[key];else extend(target[key], src[key]);
    } else {
      target[key] = src[key];
    }
  });
}

function needsNavigation() {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return params.navigation && typeof params.navigation.nextEl === 'undefined' && typeof params.navigation.prevEl === 'undefined';
}

function needsPagination() {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return params.pagination && typeof params.pagination.el === 'undefined';
}

function needsScrollbar() {
  let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return params.scrollbar && typeof params.scrollbar.el === 'undefined';
}

function uniqueClasses() {
  let classNames = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  const classes = classNames.split(' ').map(c => c.trim()).filter(c => !!c);
  const unique = [];
  classes.forEach(c => {
    if (unique.indexOf(c) < 0) unique.push(c);
  });
  return unique.join(' ');
}

/* underscore in name -> watch for changes */
const paramsList = ['modules', 'init', '_direction', 'touchEventsTarget', 'initialSlide', '_speed', 'cssMode', 'updateOnWindowResize', 'resizeObserver', 'nested', 'focusableElements', '_enabled', '_width', '_height', 'preventInteractionOnTransition', 'userAgent', 'url', '_edgeSwipeDetection', '_edgeSwipeThreshold', '_freeMode', '_autoHeight', 'setWrapperSize', 'virtualTranslate', '_effect', 'breakpoints', '_spaceBetween', '_slidesPerView', 'maxBackfaceHiddenSlides', '_grid', '_slidesPerGroup', '_slidesPerGroupSkip', '_slidesPerGroupAuto', '_centeredSlides', '_centeredSlidesBounds', '_slidesOffsetBefore', '_slidesOffsetAfter', 'normalizeSlideIndex', '_centerInsufficientSlides', '_watchOverflow', 'roundLengths', 'touchRatio', 'touchAngle', 'simulateTouch', '_shortSwipes', '_longSwipes', 'longSwipesRatio', 'longSwipesMs', '_followFinger', 'allowTouchMove', '_threshold', 'touchMoveStopPropagation', 'touchStartPreventDefault', 'touchStartForcePreventDefault', 'touchReleaseOnEdges', 'uniqueNavElements', '_resistance', '_resistanceRatio', '_watchSlidesProgress', '_grabCursor', 'preventClicks', 'preventClicksPropagation', '_slideToClickedSlide', '_preloadImages', 'updateOnImagesReady', '_loop', '_loopAdditionalSlides', '_loopedSlides', '_loopedSlidesLimit', '_loopFillGroupWithBlank', 'loopPreventsSlide', '_rewind', '_allowSlidePrev', '_allowSlideNext', '_swipeHandler', '_noSwiping', 'noSwipingClass', 'noSwipingSelector', 'passiveListeners', 'containerModifierClass', 'slideClass', 'slideBlankClass', 'slideActiveClass', 'slideDuplicateActiveClass', 'slideVisibleClass', 'slideDuplicateClass', 'slideNextClass', 'slideDuplicateNextClass', 'slidePrevClass', 'slideDuplicatePrevClass', 'wrapperClass', 'runCallbacksOnInit', 'observer', 'observeParents', 'observeSlideChildren', // modules
'a11y', '_autoplay', '_controller', 'coverflowEffect', 'cubeEffect', 'fadeEffect', 'flipEffect', 'creativeEffect', 'cardsEffect', 'hashNavigation', 'history', 'keyboard', 'lazy', 'mousewheel', '_navigation', '_pagination', 'parallax', '_scrollbar', '_thumbs', 'virtual', 'zoom'];

function getParams() {
  let obj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let splitEvents = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  const params = {
    on: {}
  };
  const events = {};
  const passedParams = {};
  extend(params, Swiper$1.defaults);
  extend(params, Swiper$1.extendedDefaults);
  params._emitClasses = true;
  params.init = false;
  const rest = {};
  const allowedParams = paramsList.map(key => key.replace(/_/, ''));
  const plainObj = Object.assign({}, obj);
  Object.keys(plainObj).forEach(key => {
    if (typeof obj[key] === 'undefined') return;

    if (allowedParams.indexOf(key) >= 0) {
      if (isObject(obj[key])) {
        params[key] = {};
        passedParams[key] = {};
        extend(params[key], obj[key]);
        extend(passedParams[key], obj[key]);
      } else {
        params[key] = obj[key];
        passedParams[key] = obj[key];
      }
    } else if (key.search(/on[A-Z]/) === 0 && typeof obj[key] === 'function') {
      if (splitEvents) {
        events[`${key[2].toLowerCase()}${key.substr(3)}`] = obj[key];
      } else {
        params.on[`${key[2].toLowerCase()}${key.substr(3)}`] = obj[key];
      }
    } else {
      rest[key] = obj[key];
    }
  });
  ['navigation', 'pagination', 'scrollbar'].forEach(key => {
    if (params[key] === true) params[key] = {};
    if (params[key] === false) delete params[key];
  });
  return {
    params,
    passedParams,
    rest,
    events
  };
}

function mountSwiper(_ref, swiperParams) {
  let {
    el,
    nextEl,
    prevEl,
    paginationEl,
    scrollbarEl,
    swiper
  } = _ref;

  if (needsNavigation(swiperParams) && nextEl && prevEl) {
    swiper.params.navigation.nextEl = nextEl;
    swiper.originalParams.navigation.nextEl = nextEl;
    swiper.params.navigation.prevEl = prevEl;
    swiper.originalParams.navigation.prevEl = prevEl;
  }

  if (needsPagination(swiperParams) && paginationEl) {
    swiper.params.pagination.el = paginationEl;
    swiper.originalParams.pagination.el = paginationEl;
  }

  if (needsScrollbar(swiperParams) && scrollbarEl) {
    swiper.params.scrollbar.el = scrollbarEl;
    swiper.originalParams.scrollbar.el = scrollbarEl;
  }

  swiper.init(el);
}

const calcLoopedSlides = (slides, swiperParams) => {
  let slidesPerViewParams = swiperParams.slidesPerView;

  if (swiperParams.breakpoints) {
    const breakpoint = Swiper$1.prototype.getBreakpoint(swiperParams.breakpoints);
    const breakpointOnlyParams = breakpoint in swiperParams.breakpoints ? swiperParams.breakpoints[breakpoint] : undefined;

    if (breakpointOnlyParams && breakpointOnlyParams.slidesPerView) {
      slidesPerViewParams = breakpointOnlyParams.slidesPerView;
    }
  }

  let loopedSlides = Math.ceil(parseFloat(swiperParams.loopedSlides || slidesPerViewParams, 10));
  loopedSlides += swiperParams.loopAdditionalSlides;

  if (loopedSlides > slides.length && swiperParams.loopedSlidesLimit) {
    loopedSlides = slides.length;
  }

  return loopedSlides;
};

function renderLoop(swiper, slides, swiperParams) {
  const modifiedSlides = slides.map((child, index) => {
    return /*#__PURE__*/React.cloneElement(child, {
      swiper,
      'data-swiper-slide-index': index
    });
  });

  function duplicateSlide(child, index, position) {
    return /*#__PURE__*/React.cloneElement(child, {
      key: `${child.key}-duplicate-${index}-${position}`,
      className: `${child.props.className || ''} ${swiperParams.slideDuplicateClass}`
    });
  }

  if (swiperParams.loopFillGroupWithBlank) {
    const blankSlidesNum = swiperParams.slidesPerGroup - modifiedSlides.length % swiperParams.slidesPerGroup;

    if (blankSlidesNum !== swiperParams.slidesPerGroup) {
      for (let i = 0; i < blankSlidesNum; i += 1) {
        const blankSlide = /*#__PURE__*/React.createElement("div", {
          className: `${swiperParams.slideClass} ${swiperParams.slideBlankClass}`
        });
        modifiedSlides.push(blankSlide);
      }
    }
  }

  if (swiperParams.slidesPerView === 'auto' && !swiperParams.loopedSlides) {
    swiperParams.loopedSlides = modifiedSlides.length;
  }

  const loopedSlides = calcLoopedSlides(modifiedSlides, swiperParams);
  const prependSlides = [];
  const appendSlides = [];

  for (let i = 0; i < loopedSlides; i += 1) {
    const index = i - Math.floor(i / modifiedSlides.length) * modifiedSlides.length;
    appendSlides.push(duplicateSlide(modifiedSlides[index], i, 'append'));
    prependSlides.unshift(duplicateSlide(modifiedSlides[modifiedSlides.length - index - 1], i, 'prepend'));
  }

  if (swiper) {
    swiper.loopedSlides = loopedSlides;
  }

  return [...prependSlides, ...modifiedSlides, ...appendSlides];
}

function getChangedParams(swiperParams, oldParams, children, oldChildren, getKey) {
  const keys = [];
  if (!oldParams) return keys;

  const addKey = key => {
    if (keys.indexOf(key) < 0) keys.push(key);
  };

  if (children && oldChildren) {
    const oldChildrenKeys = oldChildren.map(getKey);
    const childrenKeys = children.map(getKey);
    if (oldChildrenKeys.join('') !== childrenKeys.join('')) addKey('children');
    if (oldChildren.length !== children.length) addKey('children');
  }

  const watchParams = paramsList.filter(key => key[0] === '_').map(key => key.replace(/_/, ''));
  watchParams.forEach(key => {
    if (key in swiperParams && key in oldParams) {
      if (isObject(swiperParams[key]) && isObject(oldParams[key])) {
        const newKeys = Object.keys(swiperParams[key]);
        const oldKeys = Object.keys(oldParams[key]);

        if (newKeys.length !== oldKeys.length) {
          addKey(key);
        } else {
          newKeys.forEach(newKey => {
            if (swiperParams[key][newKey] !== oldParams[key][newKey]) {
              addKey(key);
            }
          });
          oldKeys.forEach(oldKey => {
            if (swiperParams[key][oldKey] !== oldParams[key][oldKey]) addKey(key);
          });
        }
      } else if (swiperParams[key] !== oldParams[key]) {
        addKey(key);
      }
    }
  });
  return keys;
}

function isChildSwiperSlide(child) {
  return child.type && child.type.displayName && child.type.displayName.includes('SwiperSlide');
}

function processChildren(c) {
  const slides = [];
  React.Children.toArray(c).forEach(child => {
    if (isChildSwiperSlide(child)) {
      slides.push(child);
    } else if (child.props && child.props.children) {
      processChildren(child.props.children).forEach(slide => slides.push(slide));
    }
  });
  return slides;
}

function getChildren(c) {
  const slides = [];
  const slots = {
    'container-start': [],
    'container-end': [],
    'wrapper-start': [],
    'wrapper-end': []
  };
  React.Children.toArray(c).forEach(child => {
    if (isChildSwiperSlide(child)) {
      slides.push(child);
    } else if (child.props && child.props.slot && slots[child.props.slot]) {
      slots[child.props.slot].push(child);
    } else if (child.props && child.props.children) {
      const foundSlides = processChildren(child.props.children);

      if (foundSlides.length > 0) {
        foundSlides.forEach(slide => slides.push(slide));
      } else {
        slots['container-end'].push(child);
      }
    } else {
      slots['container-end'].push(child);
    }
  });
  return {
    slides,
    slots
  };
}

function updateSwiper(_ref) {
  let {
    swiper,
    slides,
    passedParams,
    changedParams,
    nextEl,
    prevEl,
    scrollbarEl,
    paginationEl
  } = _ref;
  const updateParams = changedParams.filter(key => key !== 'children' && key !== 'direction');
  const {
    params: currentParams,
    pagination,
    navigation,
    scrollbar,
    virtual,
    thumbs
  } = swiper;
  let needThumbsInit;
  let needControllerInit;
  let needPaginationInit;
  let needScrollbarInit;
  let needNavigationInit;

  if (changedParams.includes('thumbs') && passedParams.thumbs && passedParams.thumbs.swiper && currentParams.thumbs && !currentParams.thumbs.swiper) {
    needThumbsInit = true;
  }

  if (changedParams.includes('controller') && passedParams.controller && passedParams.controller.control && currentParams.controller && !currentParams.controller.control) {
    needControllerInit = true;
  }

  if (changedParams.includes('pagination') && passedParams.pagination && (passedParams.pagination.el || paginationEl) && (currentParams.pagination || currentParams.pagination === false) && pagination && !pagination.el) {
    needPaginationInit = true;
  }

  if (changedParams.includes('scrollbar') && passedParams.scrollbar && (passedParams.scrollbar.el || scrollbarEl) && (currentParams.scrollbar || currentParams.scrollbar === false) && scrollbar && !scrollbar.el) {
    needScrollbarInit = true;
  }

  if (changedParams.includes('navigation') && passedParams.navigation && (passedParams.navigation.prevEl || prevEl) && (passedParams.navigation.nextEl || nextEl) && (currentParams.navigation || currentParams.navigation === false) && navigation && !navigation.prevEl && !navigation.nextEl) {
    needNavigationInit = true;
  }

  const destroyModule = mod => {
    if (!swiper[mod]) return;
    swiper[mod].destroy();

    if (mod === 'navigation') {
      currentParams[mod].prevEl = undefined;
      currentParams[mod].nextEl = undefined;
      swiper[mod].prevEl = undefined;
      swiper[mod].nextEl = undefined;
    } else {
      currentParams[mod].el = undefined;
      swiper[mod].el = undefined;
    }
  };

  updateParams.forEach(key => {
    if (isObject(currentParams[key]) && isObject(passedParams[key])) {
      extend(currentParams[key], passedParams[key]);
    } else {
      const newValue = passedParams[key];

      if ((newValue === true || newValue === false) && (key === 'navigation' || key === 'pagination' || key === 'scrollbar')) {
        if (newValue === false) {
          destroyModule(key);
        }
      } else {
        currentParams[key] = passedParams[key];
      }
    }
  });

  if (updateParams.includes('controller') && !needControllerInit && swiper.controller && swiper.controller.control && currentParams.controller && currentParams.controller.control) {
    swiper.controller.control = currentParams.controller.control;
  }

  if (changedParams.includes('children') && slides && virtual && currentParams.virtual.enabled) {
    virtual.slides = slides;
    virtual.update(true);
  } else if (changedParams.includes('children') && swiper.lazy && swiper.params.lazy.enabled) {
    swiper.lazy.load();
  }

  if (needThumbsInit) {
    const initialized = thumbs.init();
    if (initialized) thumbs.update(true);
  }

  if (needControllerInit) {
    swiper.controller.control = currentParams.controller.control;
  }

  if (needPaginationInit) {
    if (paginationEl) currentParams.pagination.el = paginationEl;
    pagination.init();
    pagination.render();
    pagination.update();
  }

  if (needScrollbarInit) {
    if (scrollbarEl) currentParams.scrollbar.el = scrollbarEl;
    scrollbar.init();
    scrollbar.updateSize();
    scrollbar.setTranslate();
  }

  if (needNavigationInit) {
    if (nextEl) currentParams.navigation.nextEl = nextEl;
    if (prevEl) currentParams.navigation.prevEl = prevEl;
    navigation.init();
    navigation.update();
  }

  if (changedParams.includes('allowSlideNext')) {
    swiper.allowSlideNext = passedParams.allowSlideNext;
  }

  if (changedParams.includes('allowSlidePrev')) {
    swiper.allowSlidePrev = passedParams.allowSlidePrev;
  }

  if (changedParams.includes('direction')) {
    swiper.changeDirection(passedParams.direction, false);
  }

  swiper.update();
}

function renderVirtual(swiper, slides, virtualData) {
  if (!virtualData) return null;
  const style = swiper.isHorizontal() ? {
    [swiper.rtlTranslate ? 'right' : 'left']: `${virtualData.offset}px`
  } : {
    top: `${virtualData.offset}px`
  };
  return slides.filter((child, index) => index >= virtualData.from && index <= virtualData.to).map(child => {
    return /*#__PURE__*/React.cloneElement(child, {
      swiper,
      style
    });
  });
}

const updateOnVirtualData = swiper => {
  if (!swiper || swiper.destroyed || !swiper.params.virtual || swiper.params.virtual && !swiper.params.virtual.enabled) return;
  swiper.updateSlides();
  swiper.updateProgress();
  swiper.updateSlidesClasses();

  if (swiper.lazy && swiper.params.lazy.enabled) {
    swiper.lazy.load();
  }

  if (swiper.parallax && swiper.params.parallax && swiper.params.parallax.enabled) {
    swiper.parallax.setTranslate();
  }
};

function useIsomorphicLayoutEffect(callback, deps) {
  // eslint-disable-next-line
  if (typeof window === 'undefined') return useEffect(callback, deps);
  return useLayoutEffect(callback, deps);
}

const SwiperSlideContext = /*#__PURE__*/createContext(null);
const SwiperContext = /*#__PURE__*/createContext(null);
const useSwiper = () => {
  return useContext(SwiperContext);
};

function _extends$1() {
  _extends$1 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends$1.apply(this, arguments);
}
const Swiper = /*#__PURE__*/forwardRef(function (_temp, externalElRef) {
  let {
    className,
    tag: Tag = 'div',
    wrapperTag: WrapperTag = 'div',
    children,
    onSwiper,
    ...rest
  } = _temp === void 0 ? {} : _temp;
  let eventsAssigned = false;
  const [containerClasses, setContainerClasses] = useState('swiper');
  const [virtualData, setVirtualData] = useState(null);
  const [breakpointChanged, setBreakpointChanged] = useState(false);
  const initializedRef = useRef(false);
  const swiperElRef = useRef(null);
  const swiperRef = useRef(null);
  const oldPassedParamsRef = useRef(null);
  const oldSlides = useRef(null);
  const nextElRef = useRef(null);
  const prevElRef = useRef(null);
  const paginationElRef = useRef(null);
  const scrollbarElRef = useRef(null);
  const {
    params: swiperParams,
    passedParams,
    rest: restProps,
    events
  } = getParams(rest);
  const {
    slides,
    slots
  } = getChildren(children);

  const onBeforeBreakpoint = () => {
    setBreakpointChanged(!breakpointChanged);
  };

  Object.assign(swiperParams.on, {
    _containerClasses(swiper, classes) {
      setContainerClasses(classes);
    }

  });

  const initSwiper = () => {
    // init swiper
    Object.assign(swiperParams.on, events);
    eventsAssigned = true;
    swiperRef.current = new Swiper$1(swiperParams);

    swiperRef.current.loopCreate = () => {};

    swiperRef.current.loopDestroy = () => {};

    if (swiperParams.loop) {
      swiperRef.current.loopedSlides = calcLoopedSlides(slides, swiperParams);
    }

    if (swiperRef.current.virtual && swiperRef.current.params.virtual.enabled) {
      swiperRef.current.virtual.slides = slides;
      const extendWith = {
        cache: false,
        slides,
        renderExternal: setVirtualData,
        renderExternalUpdate: false
      };
      extend(swiperRef.current.params.virtual, extendWith);
      extend(swiperRef.current.originalParams.virtual, extendWith);
    }
  };

  if (!swiperElRef.current) {
    initSwiper();
  } // Listen for breakpoints change


  if (swiperRef.current) {
    swiperRef.current.on('_beforeBreakpoint', onBeforeBreakpoint);
  }

  const attachEvents = () => {
    if (eventsAssigned || !events || !swiperRef.current) return;
    Object.keys(events).forEach(eventName => {
      swiperRef.current.on(eventName, events[eventName]);
    });
  };

  const detachEvents = () => {
    if (!events || !swiperRef.current) return;
    Object.keys(events).forEach(eventName => {
      swiperRef.current.off(eventName, events[eventName]);
    });
  };

  useEffect(() => {
    return () => {
      if (swiperRef.current) swiperRef.current.off('_beforeBreakpoint', onBeforeBreakpoint);
    };
  }); // set initialized flag

  useEffect(() => {
    if (!initializedRef.current && swiperRef.current) {
      swiperRef.current.emitSlidesClasses();
      initializedRef.current = true;
    }
  }); // mount swiper

  useIsomorphicLayoutEffect(() => {
    if (externalElRef) {
      externalElRef.current = swiperElRef.current;
    }

    if (!swiperElRef.current) return;

    if (swiperRef.current.destroyed) {
      initSwiper();
    }

    mountSwiper({
      el: swiperElRef.current,
      nextEl: nextElRef.current,
      prevEl: prevElRef.current,
      paginationEl: paginationElRef.current,
      scrollbarEl: scrollbarElRef.current,
      swiper: swiperRef.current
    }, swiperParams);
    if (onSwiper) onSwiper(swiperRef.current); // eslint-disable-next-line

    return () => {
      if (swiperRef.current && !swiperRef.current.destroyed) {
        swiperRef.current.destroy(true, false);
      }
    };
  }, []); // watch for params change

  useIsomorphicLayoutEffect(() => {
    attachEvents();
    const changedParams = getChangedParams(passedParams, oldPassedParamsRef.current, slides, oldSlides.current, c => c.key);
    oldPassedParamsRef.current = passedParams;
    oldSlides.current = slides;

    if (changedParams.length && swiperRef.current && !swiperRef.current.destroyed) {
      updateSwiper({
        swiper: swiperRef.current,
        slides,
        passedParams,
        changedParams,
        nextEl: nextElRef.current,
        prevEl: prevElRef.current,
        scrollbarEl: scrollbarElRef.current,
        paginationEl: paginationElRef.current
      });
    }

    return () => {
      detachEvents();
    };
  }); // update on virtual update

  useIsomorphicLayoutEffect(() => {
    updateOnVirtualData(swiperRef.current);
  }, [virtualData]); // bypass swiper instance to slides

  function renderSlides() {
    if (swiperParams.virtual) {
      return renderVirtual(swiperRef.current, slides, virtualData);
    }

    if (!swiperParams.loop || swiperRef.current && swiperRef.current.destroyed) {
      return slides.map(child => {
        return /*#__PURE__*/React.cloneElement(child, {
          swiper: swiperRef.current
        });
      });
    }

    return renderLoop(swiperRef.current, slides, swiperParams);
  }

  return /*#__PURE__*/React.createElement(Tag, _extends$1({
    ref: swiperElRef,
    className: uniqueClasses(`${containerClasses}${className ? ` ${className}` : ''}`)
  }, restProps), /*#__PURE__*/React.createElement(SwiperContext.Provider, {
    value: swiperRef.current
  }, slots['container-start'], /*#__PURE__*/React.createElement(WrapperTag, {
    className: "swiper-wrapper"
  }, slots['wrapper-start'], renderSlides(), slots['wrapper-end']), needsNavigation(swiperParams) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: prevElRef,
    className: "swiper-button-prev"
  }), /*#__PURE__*/React.createElement("div", {
    ref: nextElRef,
    className: "swiper-button-next"
  })), needsScrollbar(swiperParams) && /*#__PURE__*/React.createElement("div", {
    ref: scrollbarElRef,
    className: "swiper-scrollbar"
  }), needsPagination(swiperParams) && /*#__PURE__*/React.createElement("div", {
    ref: paginationElRef,
    className: "swiper-pagination"
  }), slots['container-end']));
});
Swiper.displayName = 'Swiper';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}
const SwiperSlide = /*#__PURE__*/forwardRef(function (_temp, externalRef) {
  let {
    tag: Tag = 'div',
    children,
    className = '',
    swiper,
    zoom,
    virtualIndex,
    ...rest
  } = _temp === void 0 ? {} : _temp;
  const slideElRef = useRef(null);
  const [slideClasses, setSlideClasses] = useState('swiper-slide');

  function updateClasses(_s, el, classNames) {
    if (el === slideElRef.current) {
      setSlideClasses(classNames);
    }
  }

  useIsomorphicLayoutEffect(() => {
    if (externalRef) {
      externalRef.current = slideElRef.current;
    }

    if (!slideElRef.current || !swiper) {
      return;
    }

    if (swiper.destroyed) {
      if (slideClasses !== 'swiper-slide') {
        setSlideClasses('swiper-slide');
      }

      return;
    }

    swiper.on('_slideClass', updateClasses); // eslint-disable-next-line

    return () => {
      if (!swiper) return;
      swiper.off('_slideClass', updateClasses);
    };
  });
  useIsomorphicLayoutEffect(() => {
    if (swiper && slideElRef.current && !swiper.destroyed) {
      setSlideClasses(swiper.getSlideClasses(slideElRef.current));
    }
  }, [swiper]);
  const slideData = {
    isActive: slideClasses.indexOf('swiper-slide-active') >= 0 || slideClasses.indexOf('swiper-slide-duplicate-active') >= 0,
    isVisible: slideClasses.indexOf('swiper-slide-visible') >= 0,
    isDuplicate: slideClasses.indexOf('swiper-slide-duplicate') >= 0,
    isPrev: slideClasses.indexOf('swiper-slide-prev') >= 0 || slideClasses.indexOf('swiper-slide-duplicate-prev') >= 0,
    isNext: slideClasses.indexOf('swiper-slide-next') >= 0 || slideClasses.indexOf('swiper-slide-duplicate-next') >= 0
  };

  const renderChildren = () => {
    return typeof children === 'function' ? children(slideData) : children;
  };

  return /*#__PURE__*/React.createElement(Tag, _extends({
    ref: slideElRef,
    className: uniqueClasses(`${slideClasses}${className ? ` ${className}` : ''}`),
    "data-swiper-slide-index": virtualIndex
  }, rest), /*#__PURE__*/React.createElement(SwiperSlideContext.Provider, {
    value: slideData
  }, zoom ? /*#__PURE__*/React.createElement("div", {
    className: "swiper-zoom-container",
    "data-swiper-zoom": typeof zoom === 'number' ? zoom : undefined
  }, renderChildren()) : renderChildren()));
});
SwiperSlide.displayName = 'SwiperSlide';

const CarouselButtonsBlock = ({ buttonPrev, buttonNext, isHideControls, }) => {
    const swiper = useSwiper();
    const onNextSlide = () => swiper.slideNext();
    const onPrevSlide = () => swiper.slidePrev();
    if (isHideControls)
        return null;
    return (jsxs("div", Object.assign({ className: "cds-carousel__button-group" }, { children: [jsx(Button, Object.assign({ disabled: swiper.isBeginning, onClick: onPrevSlide, variant: "primary-icon" }, buttonPrev, { iconPrefix: {
                    props: {
                        icon: 'arrowLeft',
                    },
                }, className: "cds-carousel__button-group--prev" }, { children: buttonPrev.children })), jsx(Button, Object.assign({ disabled: swiper.progress >= 1, onClick: onNextSlide, variant: "primary-icon" }, buttonNext, { iconPrefix: {
                    props: {
                        icon: 'arrowRight',
                    },
                } }, { children: buttonNext.children }))] })));
};

const noScriptStyles = `
  .swiper-scrollbar {
    display: none;
  }
  .cds-carousel__button-group {
    display: none;
  }
`;
const CarouselControllers = ({ progressIndicator, isHideControls, caption, buttonNext, buttonPrev, totalItems, activeSlide, scrollClassName, }) => (jsxs("div", Object.assign({ className: `cds-carousel__controllers cds-carousel__controllers--${progressIndicator}` }, { children: [progressIndicator === 'caption' && (jsx("div", Object.assign({ className: "cds-carousel__caption-block" }, { children: jsx(Typography, { tag: "span", variant: "body", size: "xs", content: `${activeSlide + 1}/${totalItems} ${caption || ''}`, className: "cds-carousel__caption-content" }) }))), progressIndicator === 'rails' && (jsx("div", { className: `swiper-scrollbar ${scrollClassName}` })), jsx(CarouselButtonsBlock, { buttonNext: buttonNext, buttonPrev: buttonPrev, isHideControls: isHideControls }), jsx("noscript", { children: jsx("style", { children: noScriptStyles }) })] })));

const checkIsNewActiveElement = ({ activeEl, activeIndex, index, previousIndex, slidesPerView, slidesLength, }) => {
    if (slidesLength <= slidesPerView)
        return false;
    return !!activeEl && activeIndex === index && previousIndex !== undefined;
};

const SLIDES_PER_VIEW_FLUID_MOBILE = 1.2;
const isHiddenElement = ({ slidesPerView, elementIndex, visibleSlidesIndexes, variant, isEnd, activeIndex, }) => {
    if (variant === 'fixed') {
        if (slidesPerView >= visibleSlidesIndexes.length)
            return false;
        if (isEnd) {
            return visibleSlidesIndexes[0] === elementIndex;
        }
        const activeIndexPosition = visibleSlidesIndexes.indexOf(activeIndex);
        const checkArr = activeIndexPosition === 0
            ? visibleSlidesIndexes.slice(0, -1)
            : visibleSlidesIndexes.slice(activeIndexPosition);
        return !checkArr.includes(elementIndex);
    }
    if (slidesPerView === SLIDES_PER_VIEW_FLUID_MOBILE) {
        return elementIndex !== activeIndex;
    }
    return false;
};

const FOCUS_ACTIVE_ELEMENT_DELAY_MS = 100;
const CarouselItem = ({ index, children, variant, slidesPerView }) => {
    const swiper = useSwiper();
    const { activeIndex, previousIndex, params, slides, isEnd } = swiper;
    const setAttributesOnHiddenElement = (element, subItems) => {
        element.setAttribute('aria-hidden', 'true');
        subItems.forEach((item) => {
            item.setAttribute('tabIndex', '-1');
            item.setAttribute('aria-hidden', 'true');
            item.style.pointerEvents = 'none';
        });
    };
    useEffect(() => {
        const currentItem = slides === null || slides === void 0 ? void 0 : slides[index];
        const activeEl = currentItem === null || currentItem === void 0 ? void 0 : currentItem.querySelector('a, button');
        if (checkIsNewActiveElement({
            activeEl,
            activeIndex,
            index,
            previousIndex,
            slidesPerView,
            slidesLength: slides.length,
        })) {
            setTimeout(() => {
                activeEl.focus();
            }, FOCUS_ACTIVE_ELEMENT_DELAY_MS);
        }
        const isFakeVisible = isHiddenElement({
            slidesPerView,
            elementIndex: index,
            variant,
            visibleSlidesIndexes: swiper.visibleSlidesIndexes,
            isEnd,
            activeIndex,
        });
        if (currentItem &&
            (!currentItem.className.includes('swiper-slide-visible') || isFakeVisible)) {
            const focusableItems = currentItem.querySelectorAll('a, button, article');
            setAttributesOnHiddenElement(currentItem, focusableItems);
            return () => {
                currentItem.removeAttribute('aria-hidden');
                focusableItems.forEach((item) => {
                    item.removeAttribute('tabIndex');
                    item.removeAttribute('aria-hidden');
                    item.style.pointerEvents = 'auto';
                });
            };
        }
        return undefined;
    }, [
        activeIndex,
        index,
        params,
        previousIndex,
        slides,
        slidesPerView,
        variant,
        isEnd,
    ]);
    return children;
};

const DEFAULT_CONTAINER_PADDING = 40;
const MOBILE_CARD_SIZE = 2;
const TABLET_CARD_SIZE = 3;
const DESKTOP_CARD_SIZE = 4;
const generateConfig = ({ variant, gap, preferredCardSize, }) => {
    if (variant === 'fixed') {
        return {
            slidesPerView: 'auto',
            slidesPerGroupAuto: true,
        };
    }
    if (variant === 'single') {
        return {
            breakpoints: {
                360: {
                    slidesPerView: 1,
                    spaceBetween: gap,
                    slidesPerGroup: 1,
                },
            },
        };
    }
    const breakpoints = {
        320: {
            slidesPerView: 1.2,
            spaceBetween: gap,
            slidesPerGroup: 1,
        },
        [preferredCardSize * MOBILE_CARD_SIZE +
            gap * MOBILE_CARD_SIZE +
            DEFAULT_CONTAINER_PADDING]: {
            slidesPerView: MOBILE_CARD_SIZE,
            spaceBetween: gap,
            slidesPerGroup: MOBILE_CARD_SIZE,
        },
        [preferredCardSize * TABLET_CARD_SIZE +
            gap * TABLET_CARD_SIZE +
            DEFAULT_CONTAINER_PADDING]: {
            slidesPerView: TABLET_CARD_SIZE,
            spaceBetween: gap,
            slidesPerGroup: TABLET_CARD_SIZE,
        },
        [preferredCardSize * DESKTOP_CARD_SIZE +
            gap * DESKTOP_CARD_SIZE +
            DEFAULT_CONTAINER_PADDING]: {
            slidesPerView: DESKTOP_CARD_SIZE,
            spaceBetween: gap,
            slidesPerGroup: DESKTOP_CARD_SIZE,
        },
    };
    return {
        breakpoints,
    };
};

const getSlidesPerViewCount = (width, gap, preferredCardSize) => {
    const count = (width + gap) / (preferredCardSize + gap);
    if (count % 1 > 0.8) {
        return Math.round(count);
    }
    return Math.floor(count);
};

const Carousel = forwardRef((props, ref) => {
    var _a, _b;
    const { className, items, progressIndicator = 'none', variant = 'fixed', gap = 32, wrapperTag = 'ul', tag = 'li', buttonNext, buttonPrev, preferredCardSize = 300, ariaLivePlaceholder, centerInsufficientSlides = true, } = props;
    const scrollClassName = `scroll-${objectHash(Object.assign(Object.assign({}, props), { items: props.items.map(({ caption }) => caption) }))}`;
    const [params, setParams] = useState({
        progress: 0,
        activeSlide: 0,
        slidesPerView: 0,
    });
    const swiperRef = useRef();
    const domDirection = useDomDirection();
    useEffect(() => {
        var _a;
        (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.changeLanguageDirection(domDirection);
    }, [domDirection]);
    const handleProgress = (swiper) => {
        if (variant === 'fixed' && !params.slidesPerView) {
            const count = getSlidesPerViewCount(swiper.width, gap, preferredCardSize);
            setParams((prevVal) => (Object.assign(Object.assign({}, prevVal), { slidesPerView: count })));
        }
        setParams((prevVal) => (Object.assign(Object.assign({}, prevVal), { progress: Math.abs(swiper.progress) })));
    };
    const handleResize = (swiper) => {
        if (variant === 'fixed') {
            const count = getSlidesPerViewCount(swiper.width, gap, preferredCardSize);
            setParams((prevVal) => (Object.assign(Object.assign({}, prevVal), { slidesPerView: count })));
        }
    };
    return (jsxs("div", Object.assign({ ref: ref, className: classNames('cds-carousel', className) }, { children: [jsxs(Swiper, Object.assign({ onSwiper: (swiper) => {
                    swiperRef.current = swiper;
                }, wrapperTag: wrapperTag, slidesPerView: "auto", watchSlidesProgress: true, spaceBetween: gap, centerInsufficientSlides: centerInsufficientSlides, shortSwipes: false, modules: [Scrollbar], scrollbar: {
                    el: `.${scrollClassName || 'swiper-scrollbar'}`,
                } }, generateConfig({
                variant,
                gap,
                preferredCardSize,
            }), { onProgress: handleProgress, onSlideChange: (swiper) => {
                    setParams((prevVal) => (Object.assign(Object.assign({}, prevVal), { activeSlide: swiper.activeIndex })));
                }, onBreakpoint: (_, breakpointParams) => {
                    setParams((prevVal) => (Object.assign(Object.assign({}, prevVal), { slidesPerView: Number(breakpointParams.slidesPerView) })));
                }, onResize: handleResize }, { children: [items.map(({ children }, index) => (jsx(SwiperSlide, Object.assign({ style: {
                            width: variant === 'fixed' ? preferredCardSize : '100%',
                        }, tag: tag, className: `cds-carousel-slide--${variant}` }, { children: jsx(CarouselItem, Object.assign({ index: index, variant: variant, slidesPerView: params.slidesPerView }, { children: children })) }), `swiper-slide${index.toString()}`))), jsx("div", Object.assign({ "aria-live": "polite", className: "cds-carousel__sr-only" }, { children: ariaLivePlaceholder(params.activeSlide + 1, items.length, params.slidesPerView, params.progress) })), items && items.length > 1 && isBrowser() ? (jsx(CarouselControllers, { progressIndicator: progressIndicator, isHideControls: ((_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.isLocked) || false, totalItems: items.length, activeSlide: params.activeSlide, caption: (_b = items[params.activeSlide]) === null || _b === void 0 ? void 0 : _b.caption, buttonNext: buttonNext, buttonPrev: buttonPrev, scrollClassName: scrollClassName })) : null] })), jsx("noscript", { children: jsx("style", { children: `.swiper-wrapper {
                  gap: ${gap}px;
            }
            
            .cds-carousel-slide--fixed,
            .cds-carousel-slide--fluid {
              width: ${preferredCardSize}px !important;
            }` }) })] })));
});

export { Carousel };
